import moment from 'moment';
import { Utils } from 'app/utils';
import { OrderUtils } from 'app/utils/order-utils';
import { BookingStatus, XpressDeliveryStatus, SpecialDiscountTypeEnum, ServiceTypeEnum, DeliveryBookingTypeEnum } from 'app/models';

export class BookingDetails {
    Id!: number;
    XpressDeliveryOrderId!: number;
    RiderFirstName!: string;
    RiderLastName!: string;
    BaseFare!: number;
    SubtotalAmount!: number;
    StatusId!: number;
	StatusText!: string;
	ServiceTypeId!: number;
	ServiceTypeText!: string;
	VehicleTypeId!: number;
    VehicleTypeText!: string;
    MeteredFare!: number;
    PickupTime!: Date | null;
    DroppedOffTime!: Date | null;
    UserCreatedAt!: Date;
    OrderRequested!: Date;
    OrderAccepted!: Date | null;
    OrderDateTime!: Date | null;
    OrderAttempts!: any[];
    OrderChats!: any[];
    DropoffOrderTimes!: any[];
    UserRating!: number;
    TotalSpend!: number;
    PickupSurgeAmount!: number;
    WaitingFeeAmount!: number;
    RiderId!: number;
    PickupAddress!: any;
    DropoffOrderPoints!: any[];
    IsPriority!: boolean;
    IsCustomerAcceptWaitingTime!: boolean;
    WaitingTimeInSec!: number;
    ArrivedToPickupAt!: Date | null;
    SessionTime!: string;
    RiderImageUrl!: string;
    RiderGender!: string;
    RiderPhone!: string;
    RiderEmail!: string;
    RiderBirthDate!: Date | null;
    UserImageUrl!: string;
    UserId!: number;
    UserFirstName!: string;
    UserLastName!: string;
    UserSignUpDaysPassed!: number;
    UserPhoneMask!: string;
    UserPhone!: string;
    VerificationStatus!: string;
    LoginProvider!: string;
    BookingsCount!: number;
    CompletedCount!: number;
    CompletedPercent!: number;
    LguFareAmount!: number;
    FlagfallAmount!: number;
    RiderXpressFee!: number;
    Tips!: number;
    Promo!: number;
    Total!: number;
    Destination!: number;
    CancellationFeeAmount!: number;
    CancelRoleText!: string;
    TripDurationCharge!: number;
    RemainingTimeSeconds!: number;
    DeliveryRecipientName!: string;
    DeliveryRecipientPhone!: string;
    PackagePhotoImageUrl!: string;
    DeliveryWeight!: string;
    DeliveryItemTypeText!: string;
    PickupPhotoImageUrl!: string;
    PickupSignatureImageUrl!: string;
    PointPickUpBuildingName!: string;
    DropPhotoImageUrl!: string;
    DropSignatureImageUrl!: string;
    PointDropOffBuildingName!: string;
    IsScanAndRide!: boolean;
	BookingType!: DeliveryBookingTypeEnum;
    SurgeCharges!: number;
    SpecialDiscountType!: SpecialDiscountTypeEnum;
    SpecialDiscountTypeText!: string;
    SpecialDiscountAmount!: number;
	IsBackToBack: boolean = false;
	BookingLabel: string = '';
	SinglePaxCount!: number;
	LuggageCount!: number;
	ScheduledAt!: Date | null;
    CanceledAt!: Date | null;
    CancelReason?: string;
    CompleteReason?: string;
    CancelRoleId!: number | null;
    AdminActionTimestamp!: Date | null;
    AdminActionedBy!: string;
	Duration!: string;
	BookingFeeAmount!: number;
	TerminalFee!: number;
	ConvenienceScheduledFeeAmount!: number;
    BoracayConvenienceFeeAmount!: number;
	PickupFareAmount!: number;
    RiderAdditionalFareAmount!: number;
    Adjustments!: number;
    WithholdingTaxAmount!: number;
    NetEarnings!: number;
    TotalNetEarnings!: number;
    RiderBaseFare!: number;

    public get TimeText() {
        return this.formatMoney(this.TripDurationCharge);
	}

	public get PickupFareAmountText() {
		return this.formatMoney(this.PickupFareAmount);
	}

	public get BookingFeeAmountText() {
		return this.formatMoney(this.BookingFeeAmount);
	}

	public get TerminalFeeAmountText() {
		return this.formatMoneyWithZero(this.TerminalFee);
	}

    public get CancellationFeeAmountText() {
        return this.formatMoney(this.CancellationFeeAmount);
    }

    public get TotalText() {
        return this.formatMoneyWithZero(this.Total);
    }

    public get MeteredFareText() {
        return this.formatMoney(this.MeteredFare);
    }

    public get PromoText() {
        return this.formatMoney(this.Promo);
    }

    public get TipsText() {
        return this.formatMoney(this.Tips);
    }

    public get XpressFeeText() {
        return this.formatMoney(this.RiderXpressFee);
    }

    public get FlagfallAmountText() {
        return this.formatMoney(this.FlagfallAmount);
    }

    public get LguFareAmountText() {
        return this.formatMoney(this.LguFareAmount);
	}

	public get ConvenienceScheduledFeeAmountText() {
		return this.formatMoney(this.ConvenienceScheduledFeeAmount);
	}

    public get BoracayConvenienceFeeAmountText() {
        return this.formatMoney(this.BoracayConvenienceFeeAmount);
    }

    public get PriorityText() {
        return (this.IsPriority) ? 'Priority' : 'Regular';
    }

    public get RiderFullName() {
        if (!this.RiderFirstName || !this.RiderLastName){
            return '-';
        }
        return `${this.RiderFirstName} ${this.RiderLastName}`;
    }

    public get ServiceText() {
        return `${this.ServiceTypeText} - ${this.VehicleTypeText}`;
    }

    public get BaseFareText() {
        return this.formatMoney(this.BaseFare);
    }

    public get RiderBaseFareText() {
        return this.formatMoney(this.RiderBaseFare);
    }

    public get AmountText() {
        return this.formatMoney(this.SubtotalAmount);
    }

    public get CreatedAtDate() {
        return this.formatDate(this.OrderDateTime);
    }

    public get OrderRequestedDate() {
        return this.formatDate(this.OrderRequested);
    }

    public get OrderAcceptedDate() {
        return this.formatDate(this.OrderAccepted);
    }

    public get PickupTimeText() {
        return this.formatTime(this.PickupTime);
    }

    public get DroppedOffTimeText() {
        return this.formatTime(this.DroppedOffTime);
    }

    public get UserCreatedAtText() {
        return this.formatDate(this.UserCreatedAt);
    }

    public get PickupSurgeAmountText() {
        return (this.PickupSurgeAmount > 0) ? `₱ ${this.PickupSurgeAmount}` : '-';
    }

    public get OrderAcceptedTimeText() {
        return this.formatTime(this.OrderAccepted);
    }

    public get UserRatingFormatted() {
        return this.UserRating.toFixed(1);
    }

    public get TotalSpendText() {
        return this.formatMoney(this.TotalSpend);
    }

    public get DriverFeeText() {
        return this.formatMoney(this.RiderAdditionalFareAmount);
    }

    public get AdjustmentsText() {
        return this.formatMoney(this.Adjustments);
    }

    public get WithholdingTaxAmountText() {
        return this.formatMoney(this.WithholdingTaxAmount);
    }

    public get RiderXpressFeeText() {
        return this.formatMoney(this.RiderXpressFee);
    }

    public get NetEarningsText() {
        return this.formatMoney(this.NetEarnings);
    }

    public get TotalNetEarningsText() {
        return this.formatMoney(this.TotalNetEarnings);
    }

    public get SpecialDiscountAmountText() {
        return this.formatMoney(this.SpecialDiscountAmount);
    }

    public get LocalStatusText() {
        if (this.StatusId === XpressDeliveryStatus.Canceled) {
            return BookingStatus.Unallocated;
        }
        let statusText = OrderUtils.getActiveOrderLocalStatusText(this.StatusId);
        if (this.StatusId === XpressDeliveryStatus.ArrivedToPickup && this.IsCustomerAcceptWaitingTime) {
            statusText = BookingStatus.WaitingExtended;
        }
        return statusText;
    }

    public get WaitingFeeAmountText() {
        return (this.WaitingFeeAmount > 0) ? `₱ ${this.WaitingFeeAmount}` : '-';
    }

    public get CanceledAtFormattedTime() {
        return this.formatDateItemDetails(this.CanceledAt);
    }

    public get PickedUpFormattedTime() {
        return this.formatDateItemDetails(this.PickupTime);
    }

    public get DropOffFormattedTime() {
        return this.formatDateItemDetails(this.DroppedOffTime);
    }

    constructor(payload: { [key: string]: any }) {
        Object.assign(this, payload);
        this.Id = payload['XpressDeliveryOrderId'];

        this.formatOrderAttempts();
        this.formatOrderChats();
        this.DropoffOrderTimes = this.formatDropoffOrderTimes();
    }

    private formatOrderAttempts(): void {
        this.OrderAttempts.forEach((attempt: any) => {
            attempt.CreatedAt = moment(attempt.CreatedAt).format('hh:mm:ss');
        });
    }

    private formatOrderChats(): void {
        this.OrderChats.forEach((message: any) => {
            message.CreatedAt = Utils.utcToLocaleString(message.CreatedAt);
        });
    }

    private formatDropoffOrderTimes(): { dropoff: Date; dropOffText: string }[] {
        return this.DropoffOrderTimes.map(date => ({
            dropoff: date,
            dropOffText: this.formatTime(date)
        }));
    }

    private formatDateItemDetails(date: Date | null): string {
        return date ? moment(Utils.utcToLocalDate(date)).format('MM-DD-YY, hh:mm a') : '';
    }

    private formatDate(date: Date | null): string {
        return date ? moment(Utils.utcToLocalDate(date)).format('D MMM YYYY') : '';
    }

    private formatTime(time: Date | null): string {
        return time ? moment(Utils.utcToLocalDate(time)).format('hh:mm:ss') : '';
    }

    private formatMoney(amount: number): string {
        return amount ? `₱ ${amount.toFixed(2)}` : '';
    }

    private formatMoneyWithZero(amount: number): string {
        return `₱ ${amount.toFixed(2)}`;
    }

    public get isScanAndRide() {
        return this.IsScanAndRide;
    }

    public get surgeChargesText() {
        return this.formatMoney(this.SurgeCharges);
	}

	public get isScheduledOrder(): boolean {
		return !!this.ScheduledAt;
	}

    public get isEzemart(): boolean {
        return this.ServiceTypeId === ServiceTypeEnum.Grocery || this.ServiceTypeId === ServiceTypeEnum.Food;
    }
}
